import I18n from 'i18n-js';
import React, { useRef, useState } from 'react';
import { clsx } from 'clsx';
import Text from '@/components/ui/Text';
import { TCITooltip } from '@/components/ui/Tooltip';
import useFlags from '@/hooks/useFlags';

interface CardTitleProps {
  fullWidth?: boolean;
  // Whether the card occupies a single grid row (no thumbnail). Full-size thumbnail cards span two rows.
  halfCard?: boolean;
  linkHref: string;
  linkLabel: string;
  openInNewWindow?: boolean;
  page?: number;
  programColor: string;
  time?: string;
  topLabel?: string;
  truncateTextTooltip?: boolean;
}

export const CardTitle = ({
  fullWidth = false,
  halfCard = true,
  linkHref,
  linkLabel,
  openInNewWindow = false,
  page,
  programColor,
  time,
  topLabel,
  truncateTextTooltip = false,
}: CardTitleProps) => {
  const { accessibilityReflow } = useFlags();
  const target = openInNewWindow ? '_blank' : '';
  const isCompactNoLabel = halfCard && !topLabel;
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipOpenChange = (open: boolean) => {
    if (!open) {
      setTooltipOpen(false);
      return;
    }
    const el = linkRef.current;
    // AIDEV-NOTE: +1 tolerance for -webkit-line-clamp rounding.
    // scrollHeight reports ~1px > clientHeight on a perfect 2-line fit.
    if (el && el.scrollHeight > el.clientHeight + 1) setTooltipOpen(true);
  };

  // Uses `md:` rather than `reflow-narrow:` — at narrower widths the topLabel overflows vertically and
  // displaces the bottom border.
  const link = (
    <a
      ref={truncateTextTooltip ? linkRef : undefined}
      aria-label={linkLabel}
      className={clsx(
        'tw-bg-white hover:tw-text-very-dark-grey tw-text-base tw-leading-[1.3rem]',
        'tw-line-clamp-2 md:tw-text-lg md:tw-leading-[1.75rem]',
      )}
      href={linkHref}
      target={target}
    >
      {linkLabel}
    </a>
  );

  return (
    <div className={clsx('tw-h-full tw-px-4 tw-my-auto',
      {
        '!tw-px-0': fullWidth && accessibilityReflow,
        'tw-pt-2': topLabel,
        'tw-pt-3': !fullWidth && isCompactNoLabel,
      })}
    >
      {topLabel && (
        <Text
          className={clsx('tw-text-very-dark-grey', {
            'tw-mb-0': !accessibilityReflow,
            'tw-mb-1 reflow-narrow:tw-text-sm': accessibilityReflow,
          })}
          size="xs"
          tag="span"
        >
          {topLabel}
        </Text>
      )}
      <Text
        className={clsx(
          'tw-text-tci-purple', {
            '!tw-my-auto': fullWidth && !page && !time,
          },
        )}
        tag="div"
        variant="heading4"
      >
        {truncateTextTooltip ? (
          <TCITooltip open={tooltipOpen} onOpenChange={handleTooltipOpenChange}>
            <TCITooltip.Trigger asChild>{link}</TCITooltip.Trigger>
            <TCITooltip.Content aria-hidden="true">{linkLabel}</TCITooltip.Content>
          </TCITooltip>
        ) : link}
      </Text>
      <div
        className={clsx(
          'tw-flex tw-text-sm tw-absolute tw-font-tci', {
            'tw-bottom-2 md:tw-bottom-0': !fullWidth,
            'tw-bottom-2.5': fullWidth,
          },
        )}
      >
        {page && (
          <p className="tw-mr-8">
            <Text
              className="tw-mr-2 tw-uppercase tw-bg-white"
              size="xs"
              style={{ color: `#${programColor}` }}
              tag="span"
              variant="label"
            >
              {I18n.t('page')}
            </Text>
            <span className="tw-bg-white tw-text-very-dark-grey">
              {page || '-'}
            </span>
          </p>
        )}
        {time && (
          <p>
            <Text
              className="tw-mr-2 tw-uppercase tw-bg-white"
              size="xs"
              style={{ color: `#${programColor}` }}
              tag="span"
              variant="label"
            >
              {I18n.t('time')}
            </Text>
            <span className="tw-bg-white tw-text-very-dark-grey">
              {time || '-'}
            </span>
          </p>
        )}
      </div>
    </div>
  );
};

export default CardTitle;
